import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

const CONNECT_TIMEOUT_MS = 20000;

/**
 * HLS live player with auto-reconnect and visible error state.
 */
export default function CctvPlayer({ hlsUrl, title = "Camera", onError, onStatusChange, className = "" }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const isLiveRef = useRef(false);
  const [status, setStatus] = useState("idle");
  const [errorHint, setErrorHint] = useState("");

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return undefined;

    let destroyed = false;
    let retryTimer;
    let connectTimer;

    const setSt = (s, hint = "") => {
      if (!destroyed) {
        if (s === "live") isLiveRef.current = true;
        if (s === "connecting") isLiveRef.current = false;
        setStatus(s);
        if (hint) setErrorHint(hint);
      }
    };

    const clearConnectTimer = () => {
      if (connectTimer) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }
    };

    const attach = () => {
      if (destroyed) return;
      setErrorHint("");
      setSt("connecting");
      clearConnectTimer();
      connectTimer = setTimeout(() => {
        if (!destroyed && !isLiveRef.current) {
          const hint =
            "No video from camera — wrong RTSP URL/password, or RTSP disabled on device. Test in VLC, then set Full RTSP URL in Cameras.";
          setSt("error", hint);
          onError?.(new Error(hint));
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          retryTimer = setTimeout(attach, 5000);
        }
      }, CONNECT_TIMEOUT_MS);

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
          manifestLoadingTimeOut: 15000,
          manifestLoadingMaxRetry: 2,
        });
        hlsRef.current = hls;
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          clearConnectTimer();
          setSt("live");
          video.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            clearConnectTimer();
            const hint =
              data.type === Hls.ErrorTypes.NETWORK_ERROR
                ? "HLS network error — check tunnel/HLS URL in CCTV settings."
                : "HLS playback failed — camera may not be streaming to MediaMTX.";
            setSt("error", hint);
            onError?.(new Error(hint));
            hls.destroy();
            hlsRef.current = null;
            retryTimer = setTimeout(attach, 5000);
          }
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = hlsUrl;
        video.addEventListener(
          "loadedmetadata",
          () => {
            clearConnectTimer();
            setSt("live");
            video.play().catch(() => {});
          },
          { once: true }
        );
        video.addEventListener(
          "error",
          () => {
            clearConnectTimer();
            setSt("error", "Native HLS failed");
            retryTimer = setTimeout(attach, 5000);
          },
          { once: true }
        );
      } else {
        clearConnectTimer();
        setSt("error", "HLS not supported in this browser");
        onError?.(new Error("HLS not supported"));
      }
    };

    attach();

    return () => {
      destroyed = true;
      isLiveRef.current = false;
      clearConnectTimer();
      clearTimeout(retryTimer);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (video) video.removeAttribute("src");
    };
  }, [hlsUrl, onError]);

  return (
    <div className={`relative overflow-hidden rounded-lg bg-black ${className}`}>
      <video ref={videoRef} className="h-full w-full object-contain" playsInline muted autoPlay />
      <div className="pointer-events-none absolute left-2 top-2 flex max-w-[90%] flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="rounded bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">{title}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
              status === "live"
                ? "bg-emerald-600 text-white"
                : status === "error"
                  ? "bg-rose-600 text-white"
                  : "bg-amber-600 text-black"
            }`}
          >
            {status}
          </span>
        </div>
        {errorHint && status === "error" && (
          <span className="rounded bg-black/70 px-2 py-1 text-[10px] leading-snug text-rose-200">{errorHint}</span>
        )}
      </div>
    </div>
  );
}
